import http from './http';

/**
 * User Agents API — Phase 1+2 of the Agent Platform.
 *
 * A User Agent is a per-user virtual assistant that owns a module/category.
 * It is a *profile*: name + job + a HIDDEN server-side system_prompt + the
 * user's own `user_focus` prompt, plus a heartbeat. The entity is a User Agent
 * — never a "manager".
 *
 * CONTRACT (built in parallel by the backend agent):
 *   GET    /api/v1/agent-templates      → templates WITHOUT system_prompt
 *   GET    /api/v1/user-agents          → the caller's agents
 *   POST   /api/v1/user-agents          → create from a template
 *   PATCH  /api/v1/user-agents/{id}     → edit name/job/focus/heartbeat/status
 *   POST   /api/v1/user-agents/{id}/wake→ immediate heartbeat
 *   GET    /api/v1/admin/user-agents?user_id=  (owner/admin)
 *   PATCH  /api/v1/admin/user-agents/{id}      (owner/admin)
 *
 * `system_prompt` is NEVER requested or rendered anywhere in this module.
 */

export type AgentScope = 'user_facing' | 'system_only';
export type AgentStatus = 'enabled' | 'paused' | 'archived';

/** A platform-authored agent blueprint. `system_prompt` is intentionally absent. */
export interface AgentTemplate {
  key: string;
  name: string;
  category: string;
  job: string;
  /** Default personality/style for this template (visible). */
  persona?: string | null;
  /** Structured persona (tone / quirks / address_form) as JSON text. */
  persona_json?: string | null;
  /** Core template (auto-provisioned; an add-on can never be core). */
  is_core?: boolean;
  capabilities?: string[];
  engine?: string;
  scope: AgentScope;
  default_heartbeat_seconds: number;
  min_heartbeat_seconds: number;
  max_heartbeat_seconds: number;
}

/** A per-user agent instance (profile). Never carries `system_prompt`. */
export interface UserAgent {
  id: string;
  category: string;
  template_key: string;
  name: string;
  job: string;
  /** The user-set focus prompt — the ONLY prompt the user sees. */
  user_focus: string | null;
  /** Personality/style — how the agent speaks (visible + editable). */
  persona: string | null;
  /** Structured persona (tone / quirks / address_form) as JSON text. */
  persona_json: string | null;
  /** The engineered heartbeat protocol (read-only; the routine wake). */
  heartbeat_prompt?: string | null;
  /** Core agent (locked: cannot be changed/deleted, only made inactive). */
  is_core?: boolean;
  /** Declared capability scopes this agent may use. */
  capabilities?: string[];
  /** AGENT JOBS (owner 2026-10-22): where the job runs. */
  runner?: 'platform' | 'remote_agent' | 'any_available';
  assigned_remote_agent_id?: string | null;
  /** ROLE-HIRING ENVELOPE (owner addendum 2026-10-22). */
  expectations?: string | null;
  thought_process?: string | null;
  resources?: string | null;
  memories?: string | null;
  /** Execution engine backing the agent (default "saik"). */
  engine?: string;
  heartbeat_seconds: number;
  heartbeat_enabled: boolean;
  /** Per-agent DAILY token cap. null/absent = unlimited (default OFF). */
  daily_token_limit?: number | null;
  /** Tokens this agent has used today (belongs to ``tokens_date``). */
  tokens_used_today?: number;
  tokens_date?: string | null;
  status: AgentStatus;
  last_heartbeat_at: string | null;
  next_heartbeat_at: string | null;
  queue_depth: number;
  open_escalations: number;
  /** Optional extras some backends include; not required. */
  scope?: AgentScope;
  user_id?: string;
}

export interface CreateUserAgentInput {
  template_key: string;
  runner?: 'platform' | 'remote_agent' | 'any_available';
  assigned_remote_agent_id?: string | null;
  name?: string;
  job?: string;
  user_focus?: string;
  persona?: string;
  persona_json?: string;
  heartbeat_seconds?: number;
  /** Per-JOB daily token cap (owner 2026-10-22). null/absent = unlimited. */
  daily_token_limit?: number | null;
  expectations?: string;
  thought_process?: string;
  resources?: string;
  memories?: string;
}

export interface UpdateUserAgentInput {
  name?: string;
  runner?: 'platform' | 'remote_agent' | 'any_available';
  assigned_remote_agent_id?: string | null;
  job?: string;
  user_focus?: string;
  persona?: string;
  persona_json?: string;
  heartbeat_seconds?: number;
  heartbeat_enabled?: boolean;
  /** Daily token cap: a number to set it, or null to clear back to unlimited. */
  daily_token_limit?: number | null;
  expectations?: string | null;
  thought_process?: string | null;
  resources?: string | null;
  memories?: string | null;
  status?: AgentStatus;
}

/** ── Escalations ("needs you") — Phase 5 ─────────────────────────────── */
export type EscalationStatus = 'open' | 'answered' | 'resolved' | 'dismissed' | 'expired';
export type EscalationSeverity = 'urgent' | 'high' | 'normal' | 'low';
export type EscalationType = 'blocker' | 'decision' | 'input_needed' | 'approval';

export interface EscalationOption {
  label: string;
  value: string;
}

/** A single "needs you" item raised by a User Agent (never carries prompts). */
export interface AgentEscalationItem {
  id: string;
  agent_id: string;
  agent_name: string | null;
  task_id: string | null;
  run_id: string | null;
  escalation_type: EscalationType;
  severity: EscalationSeverity;
  title: string | null;
  summary: string | null;
  question: string | null;
  proposed_answer: string | null;
  options: EscalationOption[];
  status: EscalationStatus;
  ring_count: number;
  ring_sent_at: string | null;
  answer_note: string | null;
  created_at: string | null;
  resolved_at: string | null;
  expires_at: string | null;
}

export interface NeedsYouSummary {
  open_count: number;
  urgent_count: number;
  closed_count: number;
  last_escalation_at: string | null;
}

/** Categories that belong to the system-only agents (design §3). */
export const SYSTEM_AGENT_CATEGORIES = ['ops', 'dispatcher', 'core'] as const;

/** The primary/main assistant category (owner directive 2026-10-01). */
export const MAIN_AGENT_CATEGORY = 'main';

/** Route to the main assistant's avatar + persona settings. */
export const MAIN_AGENT_SETTINGS_PATH = '/settings/virtual-assistant';

export function isMainAgent(agent: Pick<UserAgent, 'category'>): boolean {
  return agent.category === MAIN_AGENT_CATEGORY;
}

/** Whether an agent is system-only, using `scope` when present and falling
 *  back to the known system category set. */
export function isSystemAgent(agent: Pick<UserAgent, 'scope' | 'category'>): boolean {
  if (agent.scope) return agent.scope === 'system_only';
  return (SYSTEM_AGENT_CATEGORIES as readonly string[]).includes(agent.category);
}

/** Some backends return a bare array, others wrap it. Normalise both. */
function unwrapList(data: unknown): UserAgent[] {
  if (Array.isArray(data)) return data as UserAgent[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const inner = obj.agents ?? obj.user_agents ?? obj.items ?? obj.results;
    if (Array.isArray(inner)) return inner as UserAgent[];
  }
  return [];
}

/** Admin-only system-agents section: system templates + any instances. */
export interface SystemOnlySection {
  templates: AgentTemplate[];
  instances: UserAgent[];
}

export const userAgentsApi = {
  listTemplates: () =>
    http.get<AgentTemplate[]>('/agent-templates').then(r => r.data),

  list: () => http.get('/user-agents').then(r => unwrapList(r.data)),

  create: (data: CreateUserAgentInput) =>
    http.post<UserAgent>('/user-agents', data).then(r => r.data),

  update: (id: string, data: UpdateUserAgentInput) =>
    http.patch<UserAgent>(`/user-agents/${id}`, data).then(r => r.data),

  wake: (id: string) =>
    http.post<{ ok?: boolean }>(`/user-agents/${id}/wake`).then(r => r.data),

  // ── Escalations ("needs you") — Phase 5 ────────────────────────
  listEscalations: (params?: { status?: string; agent_id?: string; limit?: number }) =>
    http
      .get<AgentEscalationItem[]>('/user-agents/escalations', { params })
      .then(r => (Array.isArray(r.data) ? r.data : [])),

  needsYou: () =>
    http.get<NeedsYouSummary>('/user-agents/escalations/needs-you').then(r => r.data),

  answerEscalation: (id: string, body: { answer?: string; value?: string; note?: string }) =>
    http.post<AgentEscalationItem>(`/user-agents/escalations/${id}/answer`, body).then(r => r.data),

  dismissEscalation: (id: string, note?: string) =>
    http.post<AgentEscalationItem>(`/user-agents/escalations/${id}/dismiss`, { note }).then(r => r.data),

  ringEscalation: (id: string) =>
    http.post<AgentEscalationItem>(`/user-agents/escalations/${id}/ring`).then(r => r.data),

  // ── Admin (owner/admin) ────────────────────────────────────────
  adminList: (userId?: string) =>
    http
      .get('/admin/user-agents', { params: userId ? { user_id: userId } : {} })
      .then(r => unwrapList(r.data)),

  adminUpdate: (id: string, data: UpdateUserAgentInput) =>
    http.patch<UserAgent>(`/admin/user-agents/${id}`, data).then(r => r.data),

  /** Admin-only section of system agents (ops / coordinator / core). This
   *  endpoint exists in the shipped backend and fulfils design §8. */
  adminSystemOnly: (userId?: string) =>
    http
      .get<SystemOnlySection>('/admin/user-agents/system-only', {
        params: userId ? { user_id: userId } : {},
      })
      .then(r => r.data),
};

/** Human-readable heartbeat interval, e.g. 300 → "5 min", 86400 → "24 h". */export function formatInterval(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const min = seconds / 60;
  if (min < 60) return `${Math.round(min)} min`;
  const hr = min / 60;
  if (hr < 24) return `${Number.isInteger(hr) ? hr : hr.toFixed(1)} h`;
  const day = hr / 24;
  return `${Number.isInteger(day) ? day : day.toFixed(1)} d`;
}

/** Clamp a heartbeat value into a template's [min, max] bounds. */
export function clampHeartbeat(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}
